import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { TopBar } from "../components/Navigation";
import { MultiImageUploader } from "../components/ImageUploader";
import { toast } from "sonner";

const CATEGORIES = ["backpacks", "shelter", "climbing", "kayak", "footwear", "bike", "gear"];
const CONDITIONS = ["new", "like-new", "used", "for-parts"];

export default function CreateListing() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        title: "",
        description: "",
        price: "",
        category: "gear",
        condition: "used",
        location: "",
        images: [],
        cover_image: "",
    });
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/marketplace", {
                ...form,
                price: parseFloat(form.price),
            });
            toast.success("Listing created");
            navigate(`/marketplace/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <TopBar title="New listing" />
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                <div className="hidden lg:block mb-8">
                    <h1 className="font-display font-black text-4xl text-midnight-pine tracking-tight">Create a listing</h1>
                </div>

                <form onSubmit={submit} className="space-y-5">
                    <L label="Title">
                        <input
                            data-testid="listing-title-input"
                            required
                            value={form.title}
                            onChange={set("title")}
                            className="w-full px-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20"
                        />
                    </L>
                    <L label="Description">
                        <textarea
                            data-testid="listing-desc-input"
                            rows={5}
                            value={form.description}
                            onChange={set("description")}
                            className="w-full px-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20 resize-none"
                        />
                    </L>
                    <div className="grid grid-cols-2 gap-4">
                        <L label="Price (USD)">
                            <input
                                data-testid="listing-price-input"
                                type="number"
                                step="0.01"
                                required
                                value={form.price}
                                onChange={set("price")}
                                className="w-full px-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20"
                            />
                        </L>
                        <L label="Location">
                            <input
                                data-testid="listing-location-input"
                                value={form.location}
                                onChange={set("location")}
                                className="w-full px-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20"
                            />
                        </L>
                    </div>
                    <L label="Category">
                        <div className="flex flex-wrap gap-2">
                            {CATEGORIES.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    data-testid={`listing-cat-${c}`}
                                    onClick={() => setForm((f) => ({ ...f, category: c }))}
                                    className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize transition-all ${
                                        form.category === c ? "bg-ember-orange text-white" : "bg-snowcap border border-ridge-line text-granite hover:border-ember-orange"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </L>
                    <L label="Condition">
                        <div className="flex gap-2">
                            {CONDITIONS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    data-testid={`listing-cond-${c}`}
                                    onClick={() => setForm((f) => ({ ...f, condition: c }))}
                                    className={`flex-1 px-3 py-2 rounded-md text-xs font-bold capitalize transition-all ${
                                        form.condition === c ? "bg-midnight-pine text-white" : "bg-snowcap border border-ridge-line text-granite hover:border-midnight-pine"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </L>
                    <L label="Photos">
                        <p className="text-xs text-slate-gray mb-3">
                            Add up to 8 photos. The first one (or the one you mark with ★) is the cover shown in the marketplace grid.
                        </p>
                        <MultiImageUploader
                            testid="listing-photos"
                            value={form.images}
                            max={8}
                            coverUrl={form.cover_image}
                            onSetCover={(url) => setForm((f) => ({ ...f, cover_image: url }))}
                            onChange={(next) =>
                                setForm((f) => ({
                                    ...f,
                                    images: next,
                                    // Keep the cover valid: if the user removed the current cover,
                                    // fall back to the first remaining image (or empty when none).
                                    cover_image: next.includes(f.cover_image) ? f.cover_image : (next[0] || ""),
                                }))
                            }
                        />
                    </L>
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            data-testid="listing-cancel-btn"
                            className="px-6 py-3 rounded-md border border-ridge-line text-granite font-bold hover:bg-alpine-mist"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            data-testid="listing-submit-btn"
                            disabled={loading || !form.title || !form.price}
                            className="flex-1 py-3 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-[0.99] disabled:opacity-60 transition-all"
                        >
                            {loading ? "Posting…" : "Publish listing"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function L({ label, children }) {
    return (
        <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-2 block">{label}</label>
            {children}
        </div>
    );
}
